import RoleService from '../services/RoleService';

// Modulo separato da ProtectedRoute.js: così la logica di autorizzazione delle rotte è
// testabile senza montare il componente React.

/**
 * Rotta → permesso richiesto per aprirla.
 *
 * RBAC Fase 3, blocco 4. Ogni riga è **derivata** da
 * `scripts/rbac/endpoint_permission_map.yaml`: il permesso è quello che il backend
 * richiede all'endpoint che la pagina chiama per primo. `FrontendContractTest`, lato
 * backend, verifica riga per riga che sia così — è il test la cui assenza ha prodotto le
 * due divergenze di §2.2. Il presidio copre solo queste rotte: i permessi dei pulsanti e
 * delle voci di menù (es. `codex.create`, `queues.create`) non sono confrontati da nessun
 * test e restano verificati a mano.
 *
 * Chiavi con `:param` sono pattern; tutte le altre sono match esatti.
 *
 * `/test-validation` è l'unica voce non derivata: la pagina parla con un servizio esterno
 * via axios diretto e non chiama nessun endpoint della matrice, quindi non esiste un
 * permesso da cui ricavarla. `settings.update` è la capacità più vicina — «strumento di
 * amministrazione» — ed è una scelta, non una derivazione: come tale è nell'elenco delle
 * esenzioni di FrontendContractTest.
 */
export const ROUTE_PERMISSIONS = {
    '/queues': 'queues.read',
    '/codex-types': 'codex_types.read',
    '/registries': 'registries.read',
    '/prompts': 'prompts.read',
    '/processes': 'processes.read',
    // La rotta ammette su `settings.read`, ma `SystemSettings.js` rende un «access
    // denied» senza `settings.update`: chi avesse `settings.read` senza
    // `settings.update` vedrebbe la voce di menù, aprirebbe la pagina e troverebbe un
    // rifiuto. Oggi e' inerte perche' nel seed `settings.read` e' del solo `admin`, che
    // ha anche `settings.update`: i due permessi coincidono. Il giorno in cui
    // `settings.read` venisse concesso a un secondo ruolo, la pagina andra' resa in
    // sola lettura, con i salvataggi gated su `settings.update`.
    '/system-settings': 'settings.read',
    '/test-validation': 'settings.update',
    // Pagine elaborate (AUTO-213): gli aggregati di GET /v1/usage/pages/*.
    '/usage-pages': 'llm_usage.read',
    '/queues/:id': 'tasks.read',
    // Blocco 9 (D9): la pagina apre sul primo fra tre tab — Ruoli, Utenti, Audit log —
    // a seconda di quale permesso il chiamante possiede; ciascun tab si gata poi
    // singolarmente sul proprio permesso. La rotta deve ammettere chiunque possa aprirne
    // almeno uno, altrimenti chi avesse solo audit.read (o solo users.read) non
    // raggiungerebbe un tab che gli spetta. Prima del blocco 9 qui c'era solo
    // 'roles.read': stessa ragione, elenco piu' largo.
    // I gruppi: la pagina apre su GET /v1/groups, che chiede groups.read. La scrittura
    // (anagrafica e contenuto) e' gated sui pulsanti, non sulla rotta.
    '/groups': 'groups.read',
    '/roles-and-permissions': ['roles.read', 'users.read', 'audit.read'],
    // Il dettaglio di un ruolo: GET /v1/roles/{id} richiede lo stesso permesso dell'elenco.
    '/roles-and-permissions/roles/:id': 'roles.read',
    // Il dettaglio di un gruppo: GET /v1/groups/{id}. La scrittura e' gated sui pulsanti.
    '/groups/:id': 'groups.read',
    // Gli editor di configurazione. Il dettaglio (`:id`) si apre in lettura — GET
    // /v1/…/{id} — e il salvataggio e' gated su `.update` nella pagina: chi legge soltanto
    // vede la configurazione ma non la puo' salvare. La creazione (`/new-*`) invece non ha
    // nulla da mostrare a chi non puo' creare: la rotta chiede il permesso del POST che la
    // pagina esiste per chiamare, non quello delle letture di contorno che fa all'apertura.
    '/codex-types/:id': 'codex_types.read',
    '/new-codex-type': 'codex_types.create',
    '/prompts/:id': 'prompts.read',
    '/new-prompt': 'prompts.create',
    '/prompt-fragments/:id': 'prompts.read',
    '/new-prompt-fragment': 'prompts.create',
    '/processes/:id': 'processes.read',
    '/new-process': 'processes.create',
};

// Un pattern `/risorsa/:param` guarda ogni path sotto `/risorsa/`. Il match esatto di
// `/risorsa` resta separato, e con la normalizzazione dello slash finale "/queues/" cade su
// quello (queues.read), non sul dettaglio (tasks.read).
const ROUTE_GUARDS = Object.entries(ROUTE_PERMISSIONS).map(([path, permission]) => {
    if (path.includes(':')) {
        const prefix = path.slice(0, path.indexOf(':'));
        return { matches: (pathname) => pathname.startsWith(prefix), permission };
    }
    return { matches: (pathname) => pathname === path, permission };
});

/**
 * react-router (matchPath) fa combaciare una rotta sia con sia senza lo slash finale
 * (es. "/registries/" combacia con la route path="/registries"), quindi le guardie devono
 * vedere la stessa forma del path, altrimenti la pagina renderizza comunque senza essere
 * controllata. Non tocca la root "/".
 */
const normalizePathname = (pathname) =>
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

// Una guardia con piu' permessi ammette chi ne ha almeno uno (or): serve alle rotte come
// /roles-and-permissions, dove permessi diversi aprono tab diversi della stessa pagina.
// Le rotte a permesso singolo restano una stringa: il comportamento per loro è invariato.
const isPermitted = (permission) =>
    Array.isArray(permission)
        ? permission.some((p) => RoleService.can(p))
        : RoleService.can(permission);

/** True se l'accesso a `pathname` va negato con i permessi correnti. */
export const isRouteForbidden = (pathname) => {
    const normalized = normalizePathname(pathname);
    return ROUTE_GUARDS.some((guard) => guard.matches(normalized) && !isPermitted(guard.permission));
};
